import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import { RichTextEditor } from "@/marketing/components/ui/rich-text-editor";
import { cn } from "@/marketing/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import type { CommunityPostView } from "@/marketing/models/community";
import { communityAdminAPIClient } from "@/marketing/infrastructure/influencer/community/communityAdminAPIClient";
import { COMMUNITY_KEYS } from "@/marketing/infrastructure/influencer/community/utils/keys";

interface GuideFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // When present, the dialog edits this guide article; otherwise it creates a new one.
  guide?: CommunityPostView | undefined;
}

// An empty TipTap document serializes to this; treat it as "no body".
const EMPTY_HTML = "<p></p>";

export function GuideFormDialog({ open, onOpenChange, guide }: GuideFormDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(guide);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isPinned, setIsPinned] = useState(false);
  const [mode, setMode] = useState<"edit" | "web" | "mobile">("edit");

  useEffect(() => {
    if (!open) return;
    setTitle(guide?.title ?? "");
    setBody(guide?.body ?? "");
    setIsPinned(guide?.isPinned ?? false);
    setMode("edit");
  }, [open, guide]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...(title.trim() !== "" && { title: title.trim() }),
        body: body.trim(),
        isPinned,
      };
      if (guide) {
        return communityAdminAPIClient.updateGuide(guide.postId, payload);
      }
      return communityAdminAPIClient.createGuide(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Guide updated" : "Guide published");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.guides() });
      onOpenChange(false);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to save guide");
    },
  });

  const hasBody = body.trim() !== "" && body.trim() !== EMPTY_HTML;
  const canSubmit = hasBody && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Creator Guide" : "New Creator Guide"}</DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-max-h-[60vh] tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-1">
          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="How a Campaign Works" />
          </div>

          <div>
            <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between">
              <label className="tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
                Article <span className="tw-text-red-600">*</span>
              </label>
              <div className="tw-flex tw-gap-1 tw-rounded-lg tw-border tw-border-gray-200 tw-p-0.5">
                {(["edit", "web", "mobile"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    aria-pressed={mode === m}
                    className={cn(
                      "tw-rounded tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-capitalize",
                      mode === m ? "tw-bg-gray-900 tw-text-white" : "tw-text-gray-500 hover:tw-bg-gray-100",
                    )}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            {mode === "edit" ? (
              <RichTextEditor value={body} onChange={setBody} placeholder="Write the guide article..." />
            ) : mode === "web" ? (
              // Web render preview: how the creator app shows this article (prose typography).
              // Defense-in-depth — DOMPurify here too (server re-sanitizes on save).
              <div
                data-testid="guide-preview-web"
                className="tw-prose tw-prose-sm tw-max-w-none tw-min-h-[200px] tw-rounded-lg tw-border tw-border-gray-200 tw-px-3 tw-py-2"
                dangerouslySetInnerHTML={{ __html: body ? DOMPurify.sanitize(body) : "<p class='text-gray-400'>Nothing to preview yet.</p>" }}
              />
            ) : (
              // Mobile render preview: same HTML inside a phone frame at a real device ratio
              // (300×640 ≈ iPhone 9:19.5), screen scrolls internally — mirrors the CreatorHub WebView shell.
              <div className="tw-flex tw-justify-center tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-100 tw-py-4">
                <div
                  data-testid="guide-phone-frame"
                  className="tw-flex tw-h-[640px] tw-w-[300px] tw-flex-col tw-overflow-hidden tw-rounded-[2.5rem] tw-border-[10px] tw-border-gray-900 tw-bg-white tw-shadow-xl"
                >
                  <div className="tw-flex tw-h-8 tw-shrink-0 tw-items-center tw-justify-center tw-bg-white">
                    <div className="tw-h-5 tw-w-20 tw-rounded-full tw-bg-gray-900" />
                  </div>
                  <div
                    data-testid="guide-preview-mobile"
                    className="tw-prose tw-prose-sm tw-max-w-none tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-3"
                    dangerouslySetInnerHTML={{ __html: body ? DOMPurify.sanitize(body) : "<p class='text-gray-400'>Nothing to preview yet.</p>" }}
                  />
                </div>
              </div>
            )}
          </div>

          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
            Pin to top of the guide list
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? "Saving..." : isEdit ? "Save changes" : "Publish Guide"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
